import React from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { PiLink } from 'react-icons/pi';
import { ButtonSkeleton } from 'src/modules/shared/components/atoms/buttons/button-skeleton';
import { IntegrationActionsMenu } from 'src/modules/settings/components/molecules/integrations/integration-actions-menu';
import type { IntegrationActionMenuItem } from 'src/modules/settings/types/meli-integration-card.types';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface MeliConnectedActionsProps {
  isBusy: boolean;
  disableSaveButton: boolean;
  savingConfig: boolean;
  syncing: boolean;
  disconnecting: boolean;
  actionMenuItems: IntegrationActionMenuItem[];
  saveTooltip: string;
  saveLabel: string;
  onSaveClick: () => void;
}

const MeliConnectedActions: React.FC<MeliConnectedActionsProps> = ({
  isBusy,
  disableSaveButton,
  savingConfig,
  syncing,
  disconnecting,
  actionMenuItems,
  saveTooltip,
  saveLabel,
  onSaveClick,
}) => (
  <div className="flex items-center justify-end gap-2 flex-wrap">
    <Tooltip content={saveTooltip} placement="top" size="sm" as="span">
      <Button
        variant="primary"
        size="md"
        onClick={onSaveClick}
        disabled={disableSaveButton}
        loading={savingConfig}
        className="flex items-center gap-2"
      >
        <PiLink className="w-4 h-4" />
        {saveLabel}
      </Button>
    </Tooltip>
    <IntegrationActionsMenu
      ariaLabel="Actions"
      disabled={isBusy}
      loading={syncing || disconnecting}
      items={actionMenuItems}
    />
  </div>
);

interface MeliDisconnectedActionProps {
  canUpdate: boolean;
  startingOAuth: boolean;
  loadingIntegrations: boolean;
  connectLabel: string;
  connectShortLabel: string;
  onClick: () => void;
}

const MeliDisconnectedAction: React.FC<MeliDisconnectedActionProps> = ({
  canUpdate,
  startingOAuth,
  loadingIntegrations,
  connectLabel,
  connectShortLabel,
  onClick,
}) => (
  <Button
    variant="primary"
    size="md"
    onClick={onClick}
    disabled={!canUpdate || startingOAuth || loadingIntegrations}
    className="flex items-center gap-2 !bg-[#FFE600] hover:!bg-[#F5DB00] active:!bg-[#E6C800] !text-neutral-900"
  >
    <PiLink className="w-4 h-4" />
    <span className="hidden xl:inline">{connectLabel}</span>
    <span className="xl:hidden">{connectShortLabel}</span>
  </Button>
);

/**
 * Right-side action area for the MercadoLibre integration card.
 */
export interface MeliIntegrationCardRightActionProps {
  /** Whether the user can read integrations. */
  canRead: boolean;
  /** Whether the connection state is still loading. */
  isInitialLoading: boolean;
  /** Whether the MercadoLibre connection is active. */
  isConnected: boolean;
  /** Whether any menu-level action should be disabled. */
  isBusy: boolean;
  /** Whether the save button should be disabled. */
  disableSaveButton: boolean;
  /** Whether the save mutation is in flight. */
  savingConfig: boolean;
  /** Whether manual sync is in flight. */
  syncing: boolean;
  /** Whether disconnect is in flight. */
  disconnecting: boolean;
  /** Whether the user can update integrations. */
  canUpdate: boolean;
  /** Whether the OAuth bootstrap is in flight. */
  startingOAuth: boolean;
  /** Whether the integrations list is loading. */
  loadingIntegrations: boolean;
  /** Menu items rendered in the actions dropdown. */
  actionMenuItems: IntegrationActionMenuItem[];
  /** Translation function. */
  t: ReturnType<typeof useI18n>['t'];
  /** Called when save is requested. */
  onSaveClick: () => void;
  /** Called when OAuth start is requested. */
  onStartOAuth: () => void;
}

/**
 * Renders the MercadoLibre card action area depending on connection state.
 */
export const MeliIntegrationCardRightAction: React.FC<
  MeliIntegrationCardRightActionProps
> = ({
  canRead,
  isInitialLoading,
  isConnected,
  isBusy,
  disableSaveButton,
  savingConfig,
  syncing,
  disconnecting,
  canUpdate,
  startingOAuth,
  loadingIntegrations,
  actionMenuItems,
  t,
  onSaveClick,
  onStartOAuth,
}) => {
  if (!canRead) {
    return null;
  }

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <ButtonSkeleton size="md" variant="neutral" withIcon />
        <div className="h-9 w-10 rounded-lg border border-gray-200 bg-neutral-200 animate-pulse" />
      </div>
    );
  }

  if (isConnected) {
    return (
      <MeliConnectedActions
        isBusy={isBusy}
        disableSaveButton={disableSaveButton}
        savingConfig={savingConfig}
        syncing={syncing}
        disconnecting={disconnecting}
        actionMenuItems={actionMenuItems}
        saveTooltip={t('settings.integrations.meli.mode.saveTooltip')}
        saveLabel={t('settings.integrations.meli.mode.save')}
        onSaveClick={onSaveClick}
      />
    );
  }

  return (
    <MeliDisconnectedAction
      canUpdate={canUpdate}
      startingOAuth={startingOAuth}
      loadingIntegrations={loadingIntegrations}
      connectLabel={t('settings.integrations.meli.actions.connect')}
      connectShortLabel={t('settings.integrations.meli.actions.connectShort')}
      onClick={onStartOAuth}
    />
  );
};
